import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminStorageRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IProductType } from '../models/product-type.model';
import { ProductTypeApiService } from '../services/product-type-api.service';
import {
  LoadProductTypeById,
  CreateProductType,
  UpdateProductType,
  DeleteProductType,
  LoadProductTypes,
  LoadAllProductTypes,
  ClearCurrentProductType,
} from './product-type.actions';

export interface ProductTypeStateModel {
  productTypes: IProductType[];
  productTypesTotalPages: number;
  allProductTypes: IProductType[];
  currentProductType: IProductType;
}

const PRODUCT_TYPE_STATE_TOKEN = new StateToken<ProductTypeStateModel>('productType');

@State<ProductTypeStateModel>({
  name: PRODUCT_TYPE_STATE_TOKEN,
  defaults: {
    productTypes: [],
    productTypesTotalPages: 0,
    allProductTypes: [],
    currentProductType: <IProductType>{},
  },
})
@Injectable()
export class ProductTypeState {
  constructor(private service: ProductTypeApiService) {}

  @Action(LoadProductTypes)
  loadProductTypes(
    { getState, setState }: StateContext<ProductTypeStateModel>,
    { filter }: LoadProductTypes
  ) {
    return this.service.loadProductTypes(filter).pipe(
      tap((response: ApiResponse<IProductType[]>) => {
        const state = getState();
        setState({
          ...state,
          productTypes: response.data,
          productTypesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllProductTypes)
  loadAllProductTypes({ getState, setState }: StateContext<ProductTypeStateModel>) {
    return this.service.loadAllProductTypes().pipe(
      tap((response: IProductType[]) => {
        const state = getState();
        setState({
          ...state,
          allProductTypes: response,
        });
      })
    );
  }

  @Action(LoadProductTypeById, { cancelUncompleted: true })
  loadProductTypeById(
    { setState }: StateContext<ProductTypeStateModel>,
    { id }: LoadProductTypeById
  ) {
    return this.service.loadProductTypeById(id).pipe(
      tap((productType: IProductType) => {
        setState(
          patch({
            currentProductType: productType,
          })
        );
      })
    );
  }

  @Action(CreateProductType)
  @OnSuccess({ url: AdminStorageRoutes.ProductTypes, message: 'productTypeAdded' })
  createProductType(
    { setState }: StateContext<ProductTypeStateModel>,
    { productType }: CreateProductType
  ) {
    return this.service.createProductType(productType).pipe(
      tap((response: IProductType) => {
        setState(
          patch({
            productTypes: insertItem<IProductType>(response),
          })
        );
      })
    );
  }

  @Action(UpdateProductType)
  @OnSuccess({ url: AdminStorageRoutes.ProductTypes, message: 'productTypeUpdated' })
  updateProductType(
    { setState }: StateContext<ProductTypeStateModel>,
    { productType }: UpdateProductType
  ) {
    return this.service.updateProductType(productType).pipe(
      tap(() => {
        setState(
          patch({
            productTypes: updateItem<IProductType>(
              (toUpdate) => toUpdate?.id === productType.id,
              productType
            ),
          })
        );
      })
    );
  }

  @Action(DeleteProductType, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminStorageRoutes.ProductTypes,
    message: 'productTypeDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteProductType(ctx: StateContext<ProductTypeStateModel>, { id }: DeleteProductType) {
    return this.service.deleteProductType(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            productTypes: removeItem<IProductType>(
              (toDelete: IProductType | undefined) => toDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentProductType)
  clearCurrentProductType({ setState }: StateContext<ProductTypeStateModel>) {
    setState(
      patch({
        currentProductType: undefined,
      })
    );
  }

  @Selector()
  static getProductTypesTotalPages(state: ProductTypeStateModel) {
    return state.productTypesTotalPages;
  }

  @Selector()
  static productTypes(state: ProductTypeStateModel) {
    return state.productTypes;
  }

  @Selector()
  static allProductTypes(state: ProductTypeStateModel) {
    return state.allProductTypes;
  }

  @Selector()
  static getProductTypeById(state: ProductTypeStateModel): IProductType {
    return state.currentProductType;
  }
}
